import { ContractAbi } from 'web3';
import { ContractAddress, DepositInfo, Reward, RewardInfo, TDeposit } from '../../../shared/api/contract';

import { ConnectorType } from '@reown/appkit';
import { anyTokenSymbolAbi, contractABI } from '../../../shared/api/contract/config';
import { depositFormat } from '../../deposit/lib/deposit-info';
import { MoonStaking } from '../../../shared/api/contract/ethers/moon-staking';
import { ethers } from 'ethers';

export class GetBonusesService extends MoonStaking {
    constructor(contractAddress: ContractAddress, abi: ContractAbi, provider: ConnectorType, walletAddress: ContractAddress) {
        super(contractAddress, abi, provider, walletAddress);
    }

    async getRewards(address: ContractAddress): Promise<[string, Reward[], bigint[]]> {
        try {
            const [depositIndexes, rewardIndexes] = await this.getRewardIndexesAndWithDepositIndexes(address);
            const rewards = await this.getRewardsBatch(address, depositIndexes, rewardIndexes);

            if (!rewardIndexes.length) return ['', [], []];

            const allDepositIndexes = await this.getUserDepositIndexes(address);
            const mainReward = ethers.utils.formatEther((await this.getMainRewardBatchDeposit(address, allDepositIndexes)))
            return [mainReward, rewards, depositIndexes];
        } catch (error) {
            console.log(error);
            return ['', [], []];
        }
    }

     async getMainRewards(depositIndexes: bigint[]): Promise<string> {
        try {
            // const mainRewardsWeis = await this.contract.pendingRewardMainBatch(this.walletAddress, depositIndexes) as bigint[];
            //
            // let amount = BigInt(0);
            //
            // for (let i = 0; i < mainRewardsWeis.length; i++) {
            //     amount += (mainRewardsWeis[i])
            // }
            //
            // const mainRewardsAmount = ethers.utils.formatEther(amount)
            // console.log(Number(mainRewardsAmount))

            const mainReward = ethers.utils.formatEther((await this.getMainRewardBatchDeposit(this.walletAddress, depositIndexes)))
            return mainReward
        } catch(e) {
            console.log(e)
            return '0';
        }
    }


    private async getMainRewardBatchDeposit(address: ContractAddress, indexes: bigint[]): Promise<bigint> {
        try {
            const rewards = (await this.contract.pendingRewardMainBatch(address, indexes)) as bigint[];
            let sum = 0;
            for (let i = 0; i < rewards.length; i++) {
                // sum += parseFloat(ethers.utils.formatEther(rewards[i]));
                sum += Number(rewards[i]);
            }
            return BigInt(sum);
        } catch (error) {
            console.log(error);
            return 0n;
        }
    }

    private async getRewardIndexesAndWithDepositIndexes(address: ContractAddress): Promise<[bigint[], bigint[][]]> {
        try {
            const lastRewardIndex = Number(await this.getAdditionalRewardLastIndex());

            const depositIndexes = await this.getUserDepositIndexes(address);
            const deposits = await this.getDepositsContract(address, depositIndexes);

            const rewardIndexes: bigint[][] = [[]];
            const rewardDepositsIndexes: bigint[] = [];
            const depositIndexesForRewards: bigint[] = [];

            for (let i = 0; i < deposits.length; i++) {
                if (deposits[i].lastRewardDeposit === lastRewardIndex) continue;

                rewardDepositsIndexes.push(depositIndexes[i]);
                depositIndexesForRewards.push(depositIndexes[i]);

                for (let j = deposits[i].lastRewardDeposit; j < lastRewardIndex; j++) {
                    if (!rewardIndexes[i]) rewardIndexes[i] = [];

                    rewardIndexes[i].push(BigInt(j));
                }
            }

            return [rewardDepositsIndexes, rewardIndexes];
        } catch (error) {
            console.log(error);
            return [[], []];
        }
    }

    private async getAdditionalRewardLastIndex(): Promise<number | undefined> {
        try {
            const index = (await this.contract.getAdditionalRewardLastIndex()) as number;

            return index;
        } catch (error) {
            console.log(error);
            return undefined;
        }
    }

    private async getDepositsContract(userAddress: string, depositIndexes: bigint[]): Promise<TDeposit[]> {
        try {
            const deposits = (await this.contract.getUserDeposits(userAddress, depositIndexes)) as DepositInfo[];

            const result = deposits.map((deposit) => depositFormat(deposit));

            return result;
        } catch (error) {
            console.error('Error fetching deposits:', error);
            return [];
        }
    }

    private async getUserDepositIndexes(userAddress: string): Promise<bigint[]> {
        try {
            const indexes = (await this.contract.getUserActualDepositIndexes(userAddress)) as bigint[];

            return indexes;
        } catch (error) {
            console.error('Error fetching deposit indexes:', error);
            return [];
        }
    }

    private async getRewardsBatch(address: ContractAddress, depositIndexes: bigint[], rewadIndexes: bigint[][]): Promise<Reward[]> {
        try {
            const rewards = await this.pendingRewardsBatch(address, depositIndexes, rewadIndexes);

            const result: Reward[][] = [];

            const tasks = rewadIndexes.map(async (reward, j) => {
                // const mainReward = await this.getMainRewardDeposit(address, depositIndexes[j]);

                if (!result[j]) result[j] = [];

                // result[j].push({ amount: mainReward, symbol: 'MOON' });

                await Promise.all(
                    reward.map(async (index, i) => {
                        try {
                            const rewardInfo = await this.getRewardInfo(index);

                            if (!rewardInfo) return;

                            const rewardSymbol = await this.getMoreInfoForReward(rewardInfo.token);

                            result[j].push({ amount: rewards[j][i], symbol: rewardSymbol });
                        } catch (error) {
                            console.log(error);
                        }
                    })
                );
            });
            await Promise.all(tasks);

            return this.mergedRewards(result.flat());
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    private async getRewardInfo(rewardIndex: bigint): Promise<RewardInfo | undefined> {
        try {
            const reawardInfo = (await this.contract.rewardInfo(rewardIndex)) as RewardInfo;

            return reawardInfo;
        } catch (error) {
            console.log(error);
            return undefined;
        }
    }

    private async getMoreInfoForReward(contractAddress: string): Promise<string> {
        try {
            const contract = new ethers.Contract(contractAddress, anyTokenSymbolAbi, this.signer);

            const symbol = (await contract.symbol()) as string;

            return symbol;
        } catch (error) {
            console.log(error);
            return '';
        }
    }

    private async pendingRewardsBatch(address: ContractAddress, depositIndexes: bigint[], rewardIndexes: bigint[][]): Promise<bigint[][]> {
        try {
            const rewardInfos = (await this.contract.pendingRewardsBatch(address, depositIndexes, rewardIndexes)) as bigint[][];

            return rewardInfos;
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    private mergedRewards(rewards: Reward[]): Reward[] {
        const mergedData = Object.values(
            rewards.reduce((acc, { symbol, amount }) => {
                const am = BigInt(amount.toString());
                if (!acc[symbol]) {
                    acc[symbol] = { symbol, amount: 0n };
                }
                acc[symbol].amount += am;
                return acc;
            }, {} as Record<string, { symbol: string; amount: bigint }>)
        );

        return mergedData;
    }
}
